import { Card, CardContent, Chip, Typography, Box, Button, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: 'none',
    transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: theme.palette.primary.main,
      boxShadow: theme.palette.type === 'dark' ? '0 14px 32px rgba(0,0,0,.22)' : theme.shadows[3],
    },
  },
  hero: {
    minHeight: 102,
    padding: theme.spacing(2),
    position: 'relative',
    color: theme.palette.text.primary,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(145deg, rgba(167,123,255,.16), rgba(39,199,217,.06))'
      : 'linear-gradient(145deg, rgba(124,58,237,.10), rgba(39,199,217,.06))',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  type: { textTransform: 'uppercase', letterSpacing: 1.1, fontWeight: 800, color: theme.palette.primary.light, fontSize: '0.67rem' },
  title: { marginTop: theme.spacing(0.75), paddingRight: 32, fontWeight: 800, lineHeight: 1.22 },
  star: { position: 'absolute', right: 12, top: 12, color: theme.palette.text.secondary },
  content: { display: 'flex', flexDirection: 'column', flex: 1, padding: theme.spacing(2), '&:last-child': { paddingBottom: theme.spacing(1.5) } },
  description: {
    color: theme.palette.text.secondary,
    lineHeight: 1.55,
    minHeight: 66,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    overflow: 'hidden',
  },
  tags: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: theme.spacing(1.5) },
  tag: { height: 22, borderRadius: 5, fontSize: '0.68rem', background: theme.palette.action.hover },
  footer: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: theme.spacing(2), borderTop: `1px solid ${theme.palette.divider}` },
  owner: { display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, color: theme.palette.text.secondary, flex: 1 },
  choose: { textTransform: 'none', borderRadius: 6, fontWeight: 700 },
}));

type FluxTemplateCardProps = {
  template: Entity;
  onSelected?: (template: Entity) => void;
  additionalLinks?: Array<{ text: string; url: string }>;
};

export const FluxTemplateCard = ({ template, onSelected }: FluxTemplateCardProps) => {
  const classes = useStyles();
  const tags = template.metadata.tags ?? [];
  const type = typeof template.spec?.type === 'string' ? template.spec.type : 'software template';
  const owner = typeof template.spec?.owner === 'string' ? template.spec.owner : 'Platform Engineering';

  return (
    <Card className={classes.card}>
      <Box className={classes.hero}>
        <Typography className={classes.type}>{type}</Typography>
        <Typography variant="h6" className={classes.title}>{template.metadata.title ?? template.metadata.name}</Typography>
        <Tooltip title="Añadir a favoritos"><StarBorderIcon className={classes.star} fontSize="small" /></Tooltip>
      </Box>
      <CardContent className={classes.content}>
        <Typography variant="body2" className={classes.description}>
          {template.metadata.description ?? 'Plantilla estandarizada para crear un nuevo componente de software.'}
        </Typography>
        <Box className={classes.tags}>
          {tags.slice(0, 7).map(tag => <Chip key={tag} size="small" label={tag} className={classes.tag} />)}
        </Box>
        <Box className={classes.footer}>
          <Box className={classes.owner}>
            <PersonOutlineIcon fontSize="small" />
            <Typography variant="caption" noWrap>{owner.replace(/^group:default\//, '')}</Typography>
          </Box>
          <Button
            className={classes.choose}
            color="primary"
            variant="outlined"
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => onSelected?.(template)}
          >
            Usar plantilla
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
